import type { WheelSpin } from '@/api/types';
import { formatDateTime, formatDay, formatMoney } from '@/lib/format';
import { Badge } from '@/ui/Badge';
import { Table, Td, Th } from '@/ui/Table';
import { STATUS_HELP } from './spin-status';
import { NEEDS_ATTENTION } from './wheel-filters';

function formatDeposit(spin: WheelSpin): string {
  if (spin.depositTotal === null) return '–';
  return `${formatMoney(spin.depositTotal)} ${spin.depositCurrency ?? ''}`.trim();
}

interface SpinsTableProps {
  spins: WheelSpin[];
  onPickPlayer(playerUuid: string): void;
}

export function SpinsTable({ spins, onPickPlayer }: SpinsTableProps) {
  return (
    <Table>
      <thead>
        <tr>
          <Th align="left">Spun at</Th>
          <Th align="left">Player</Th>
          <Th align="left">Tier</Th>
          <Th align="left">Wheel</Th>
          <Th align="left">Prize</Th>
          <Th align="left">Status</Th>
          <Th>Deposited that day</Th>
          <Th align="left">Campaign</Th>
          <Th align="left">Settled</Th>
        </tr>
      </thead>
      <tbody>
        {spins.length === 0 && (
          <tr>
            <Td align="left" colSpan={9} className="text-slate-500">
              No spins match these filters.
            </Td>
          </tr>
        )}
        {spins.map((spin) => (
          <tr key={spin.id} className={NEEDS_ATTENTION.includes(spin.status) ? 'bg-red-50' : ''}>
            <Td align="left">
              {formatDateTime(spin.spunAt)}
              <span
                className="block text-xs text-slate-500"
                title="The UTC calendar day the spin counted for; the day filters use it"
              >
                UTC day {formatDay(spin.day)}
              </span>
            </Td>
            <Td align="left">
              <button
                type="button"
                className="font-mono text-xs text-blue-700 hover:underline"
                title="Show only this player's spins"
                onClick={() => onPickPlayer(spin.playerUuid)}
              >
                {spin.playerUuid}
              </button>
            </Td>
            <Td align="left">
              <Badge value={spin.tier} />
            </Td>
            <Td align="left">
              {spin.wheelUid}
              {spin.wheelTier !== spin.tier && (
                <span
                  className="ml-2 text-xs text-slate-500"
                  title="No wheel for the entitled tier; the next tier down was served"
                >
                  served {spin.wheelTier}
                </span>
              )}
            </Td>
            <Td align="left">
              {spin.segmentLabel ?? spin.segmentUid}
              {spin.segmentLabel && <span className="ml-2 font-mono text-xs text-slate-400">{spin.segmentUid}</span>}
            </Td>
            <Td align="left" title={STATUS_HELP[spin.status]}>
              <Badge value={spin.status} />
            </Td>
            <Td>{formatDeposit(spin)}</Td>
            <Td align="left" className="font-mono text-xs">
              {/* Truncated on screen only: selecting and copying still takes the whole id. */}
              <span className="block max-w-[11rem] truncate" title={spin.campaignUuid ?? undefined}>
                {spin.campaignUuid ?? '–'}
              </span>
              {spin.replacedCampaignUuid && (
                <span
                  className="block max-w-[11rem] truncate text-slate-400"
                  title={`replaced ${spin.replacedCampaignUuid}`}
                >
                  replaced {spin.replacedCampaignUuid}
                </span>
              )}
            </Td>
            <Td align="left">{formatDateTime(spin.settledAt)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
